import { DisplayText, MarketView } from '../perps-v1-shared/types';

// Shared form state + payload builder for both the basic-info Edit dialog and the
// content/order Manage Symbols dialog. The API is a FULL-OBJECT PUT
// (`PUT /perps-v1/views/:code`), so both dialogs must build the request body from a full
// `FormState` even though each one only renders a subset of its fields — see `buildViewBody`.

export const NATIVE_DEX = ''; // dexes 规则中空字符串代表 native dex

export const SORT_FIELDS = ['volume', 'change_percent', 'listed_at', 'open_interest', 'funding', 'market_cap'];
export const SORT_DIRECTIONS = ['ASC', 'DESC'];

// Display-only label keys for the raw values above — the wire values (backend
// `market_views.sort_field`/`sort_direction`, validated in xbit-hypertrader-go's
// internal/models/market_view.go) never change, only what an admin reads on screen. Keep this
// map's keys in lockstep with SORT_FIELDS/SORT_DIRECTIONS; any renderer showing one of these
// raw strings should go through here instead of printing it directly.
export const SORT_FIELD_LABEL_KEYS: Record<string, string> = {
  volume: 'module.perpsV1Views.sortFieldOptions.volume',
  change_percent: 'module.perpsV1Views.sortFieldOptions.changePercent',
  listed_at: 'module.perpsV1Views.sortFieldOptions.listedAt',
  open_interest: 'module.perpsV1Views.sortFieldOptions.openInterest',
  funding: 'module.perpsV1Views.sortFieldOptions.funding',
  // Point-in-time figure like open interest — no time-window suffix in the label, unlike volume/
  // change which are explicitly "24h".
  market_cap: 'module.perpsV1Views.sortFieldOptions.marketCap',
};

export const SORT_DIRECTION_LABEL_KEYS: Record<string, string> = {
  ASC: 'module.perpsV1Views.sortDirectionOptions.asc',
  DESC: 'module.perpsV1Views.sortDirectionOptions.desc',
};

export interface FormState {
  code: string;
  label: DisplayText;
  badge: DisplayText;
  parentCode: string;
  dexes: string[];
  sectorCodes: string[];
  includeSymbols: string[];
  excludeSymbols: string[];
  sortField: string;
  sortDirection: string;
  listingWindowDays: string;
  minVolume: string;
  minChangePercent: string;
  displayLimit: string;
  displayOrder: string;
}

// 空白默认值：与后端 DTO 必填字段对齐（sortField/sortDirection/displayOrder）
//
// `displayOrder` defaults to '1' for callers that don't care (e.g. ViewSymbolsDialog's edit-only
// placeholder state, never actually saved). Real creates MUST pass the target sibling group's
// `max(displayOrder) + 1`, computed in List.tsx from `childrenByParent` — see openCreate/
// openCreateChild — so a new view lands at the end of its group instead of colliding with the
// existing first sibling.
export function blankForm(displayOrder: string = '1'): FormState {
  return {
    code: '',
    label: {},
    badge: {},
    parentCode: '',
    dexes: [],
    sectorCodes: [],
    includeSymbols: [],
    excludeSymbols: [],
    sortField: 'volume',
    sortDirection: 'DESC',
    listingWindowDays: '',
    minVolume: '',
    minChangePercent: '',
    displayLimit: '',
    displayOrder,
  };
}

export function formFromView(view: MarketView): FormState {
  return {
    code: view.code,
    label: view.label ?? {},
    badge: view.badge ?? {},
    parentCode: view.parentCode ?? '',
    dexes: view.dexes ?? [],
    sectorCodes: view.sectorCodes ?? [],
    includeSymbols: view.includeSymbols ?? [],
    excludeSymbols: view.excludeSymbols ?? [],
    sortField: view.sortField,
    sortDirection: view.sortDirection,
    listingWindowDays: view.listingWindowDays != null ? String(view.listingWindowDays) : '',
    minVolume: view.minVolume ?? '',
    minChangePercent: view.minChangePercent ?? '',
    displayLimit: view.displayLimit != null ? String(view.displayLimit) : '',
    displayOrder: String(view.displayOrder ?? 1),
  };
}

// Seeds a blank form for "create child under this parent": copies only the parent's RULE
// fields (dexes/sectorCodes/sort/filters) plus parentCode, leaving everything else blank.
// Deliberately excludes includeSymbols/excludeSymbols — those are per-view manual mounts, and
// copying them would duplicate the parent's manual entries into the child — and excludes
// code/label/badge/displayLimit, which identify/place the child, not its rule. `displayOrder` is
// NOT copied from the parent either — it's the child's position among ITS OWN siblings, passed in
// by the caller (List.tsx's openCreateChild, via childrenByParent) same as blankForm.
// Kept next to blankForm so the "which fields are copied" decision lives in one greppable spot.
export function blankFormWithParentRule(parent: MarketView, displayOrder: string = '1'): FormState {
  return {
    ...blankForm(displayOrder),
    parentCode: parent.code,
    dexes: parent.dexes ?? [],
    sectorCodes: parent.sectorCodes ?? [],
    sortField: parent.sortField,
    sortDirection: parent.sortDirection,
    listingWindowDays: parent.listingWindowDays != null ? String(parent.listingWindowDays) : '',
    minVolume: parent.minVolume ?? '',
    minChangePercent: parent.minChangePercent ?? '',
  };
}

// DisplayText 三字段全空即视为未填写（用于 badge 省略判断）
export function isBlankDisplayText(text: DisplayText): boolean {
  return !text.slug && !text.en && !text.zh;
}

// Shared request-body builder for create/update AND the live preview — both must send the
// byte-identical payload, or the preview stops reflecting what saving would actually produce.
// Mirrors the backend DTO: empty arrays are OMITTED (sending an empty-but-non-nil array is
// rejected), not sent as `[]`.
//
// Used by BOTH the Edit dialog (basic info) and the Manage Symbols dialog (content & order) —
// each seeds this from the same `formFromView` and calls this same builder, so a save from
// either dialog always sends the full object, never just the fields it renders. Do not split
// this per-dialog.
export function buildViewBody(form: FormState) {
  const listingWindowDays = form.listingWindowDays.trim() ? Number(form.listingWindowDays) : undefined;
  const displayLimit = form.displayLimit.trim() ? Number(form.displayLimit) : undefined;

  return {
    code: form.code.trim(),
    label: form.label,
    ...(isBlankDisplayText(form.badge) ? {} : { badge: form.badge }),
    ...(form.parentCode ? { parentCode: form.parentCode } : {}),
    ...(form.dexes.length > 0 ? { dexes: form.dexes } : {}),
    ...(form.sectorCodes.length > 0 ? { sectorCodes: form.sectorCodes } : {}),
    ...(form.includeSymbols.length > 0 ? { includeSymbols: form.includeSymbols } : {}),
    ...(form.excludeSymbols.length > 0 ? { excludeSymbols: form.excludeSymbols } : {}),
    sortField: form.sortField,
    sortDirection: form.sortDirection,
    ...(listingWindowDays !== undefined ? { listingWindowDays } : {}),
    ...(form.minVolume.trim() ? { minVolume: form.minVolume.trim() } : {}),
    ...(form.minChangePercent.trim() ? { minChangePercent: form.minChangePercent.trim() } : {}),
    ...(displayLimit !== undefined ? { displayLimit } : {}),
    displayOrder: String(form.displayOrder).trim() ? Number(form.displayOrder) : 1,
  };
}
